import { Button } from "@/components/flexkit/Button";
import { useCreateWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import type { FC } from "react";
import { WorkflowTrigger, WorkflowStep } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { formatError } from "@/utils/errors";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSetAtom, useStore } from "jotai";
import { editableStepsAtom, isEditingStepsAtom } from "@/routes/automations/automation-details-page/state";
import { convertFormStepsToWorkflowStep } from "@/components/automations/automation-edit-form-data";
import { useNavigate } from "react-router-dom";
import { workflowDetailsUrl, WorkflowDetailsTab } from "@/utils/automations";

const CreateAutomationButton: FC<{ workflow: PlainWorkflow }> = ({ workflow }) => {
    const { mutateAsync: createWorkflow, isPending: isCreatingWorkflow } = useCreateWorkflow();
    const { toast } = useToast();
    const store = useStore();
    const setIsEditingSteps = useSetAtom(isEditingStepsAtom);
    const navigate = useNavigate();

    const create = async () => {
        const steps = store.get(editableStepsAtom);
        try {
            const createdWorkflow = await createWorkflow({
                name: workflow.metadata?.name || "",
                description: workflow.metadata?.description || "",
                triggers: workflow.spec?.triggers.map((trigger) => new WorkflowTrigger(trigger)),
                steps: steps ? convertFormStepsToWorkflowStep(steps).map((s) => new WorkflowStep(s)) : [],
            });
            setIsEditingSteps(false);
            navigate(workflowDetailsUrl({ id: createdWorkflow.id, tab: WorkflowDetailsTab.Definition }), {
                replace: true,
            });
        } catch (error) {
            toast({
                title: "Failed to create automation",
                description: formatError(error),
            });
        }
    };

    return (
        <Button
            variant="primary"
            onClick={create}
            data-tracking-id="create-automation-from-template"
            loading={isCreatingWorkflow}
        >
            Create
        </Button>
    );
};

export default CreateAutomationButton;
